// ========= IMPORTS ==========

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../context/LanguageProvider";


// ========= MAIN COMPONENT ==========

const IMGI1 = () => {
    console.log("------------------imgi1 rendered----------------------"); // helpful debug render log

    // ====== Hooks ======
    const navigate = useNavigate();               // used to move user to next screen
    const [selectedNumbers, setSelectedNumbers] = useState([]); // stores up to 3 chosen numbers
    const [errorMessage, setErrorMessage] = useState("");       // error prompt when not enough numbers selected
    const { translation } = useLanguage();        // translation function for i18n text keys

    // ====== Keyboard Handler ======
    // Called whenever a key is pressed (added via useEffect)
    const handleKeyPress = (event) => {
        // Check specifically for the Spacebar (" ")
        if (event.key === " ") {
            // Require exactly 3 numbers to be selected before proceeding
            if (selectedNumbers.length !== 3) {
                // Show translated error prompt from language files
                setErrorMessage(translation("IMGI1_numberSelectionPrompt"));
            } else {
                // Build an array of all possible numbers [0..9]
                const allNumbers = Array.from({ length: 10 }, (_, index) => index);

                // Remove selected numbers → gives the unselected list
                const unSelectedNumbers = allNumbers.filter(
                    number => !selectedNumbers.includes(number)
                );
                // Proceed to the next step or navigate
                navigate("/IMG1", { state: { selectedNumbers, unSelectedNumbers } });
            }
        }
    };

    // ====== Checkbox Click Handler ======
    const handleCheckboxChange = (number) => {
        // State updater form to work off the previous selection array
        setSelectedNumbers((prevSelected) => {
            if (prevSelected.includes(number)) {
                // Already selected → remove it (toggle off)
                return prevSelected.filter((n) => n !== number);
            } else if (prevSelected.length < 3) {
                // Not selected and under 3 selections → add it
                return [...prevSelected, number];
            }
            // Already have 3 selections → ignore this click unless it was on already-selected box
            return prevSelected;
        });
    };

    // ====== Side Effect: Keyboard Listener ======
    useEffect(() => {
        // Attach the keyboard event listener to the global window
        window.addEventListener("keydown", handleKeyPress);

        // Cleanup function to remove listener when component unmounts or dependencies change
        return () => {
            window.removeEventListener("keydown", handleKeyPress);
        };
    // Adding selectedNumbers here so the latest selection is checked when pressing space
    }, [selectedNumbers]);

    // ====== Render ======
    return (
        <div className="flex flex-col items-center justify-center h-screen bg-gray-100 ">
            {/* Inner container for text and number selection UI */}
            <div className="flex flex-col items-center justify-center relative">

                {/* Heading: congratulatory / intro message */}
                <h1 className="text-4xl text-gray-800 mb-5 text-left w-full max-w-6xl ml-4">
                    {translation("IMGI1_congratulationsMessage")}
                </h1>

                {/* Heading: test instructions */}
                <h2 className="text-4xl text-gray-800 mb-8 text-left w-full max-w-6xl ml-4">
                    {translation("IMGI1_testInstructions")}
                </h2>
                
                {/* Instruction list */}
                <ul className="list-disc mb-6 w-full max-w-5xl ml-4 pl-2 text-left text-2xl">
                    
                    {/* Prompt to select favourite numbers
                        Using dangerouslySetInnerHTML: content may include HTML formatting such as <b>, <i>
                        SECURITY: Ensure translation values here are trusted/sanitised
                    */}
                    <li
                        className="mb-2"
                        dangerouslySetInnerHTML={{
                            __html: translation("IMGI1_favoriteNumbersPrompt")
                        }}
                    />

                    {/* Number selection grid (0 to 9) */}
                    <div className="grid grid-cols-3 gap-4">
                        {Array.from({ length: 10 }, (_, index) => (
                            <label key={index} className="flex items-center text-2xl font-medium">
                                <input
                                    type="checkbox"
                                    value={index}
                                    checked={selectedNumbers.includes(index)}
                                    onChange={() => handleCheckboxChange(index)}
                                    // Disable boxes if already 3 selected, except for boxes that are currently selected (so user can uncheck)
                                    disabled={selectedNumbers.length >= 3 && !selectedNumbers.includes(index)}
                                    className="mr-2"
                                />
                                {index}
                            </label>
                        ))}
                    </div>

                    {/* Other instruction list items */}
                    <li className="mb-2">{translation("IMGI1_numberAppearancePrompt")}</li>
                    <li className="mb-2">{translation("IMGI1_taskInstructions")}</li>
                    <li className="mb-2">{translation("IMGI1_noPressInstruction")}</li>
                    <li className="mb-2">{translation("IMGI1_speedAccuracyReminder")}</li>
                    <li className="mb-2">{translation("IMGI1_warningResponseSpeed")}</li>
                </ul>

                {/* Prompt to begin */}
                <h2 className="text-3xl text-gray-800 mb-8 text-left w-full max-w-6xl ml-4">
                    {translation("IMGI1_beginPrompt")}
                </h2>

                {/* Error message display (appears if user tries to start without selecting 3 numbers) */}
                {errorMessage && (
                    <span className="absolute -bottom-5 text-red-600 text-2xl font-bold">
                        {errorMessage}
                    </span>
                )}
            </div>
        </div>
    );
};

export default IMGI1;
